import { getCurrencySymbol } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { VoucherService } from '@/app/services/voucher/voucher.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { getAdminSettingsToolPath } from '../../../admin-tool-registry/admin-tool.registry';

type CurrencyCodeOption = 'usd' | 'gbp' | 'eur';

type FlagSelectOption<T extends string> = {
  value: T;
  label: string;
  flagUrl: string;
};

const FLAG_BASE = 'https://shout-images-static.s3.eu-west-1.amazonaws.com';

const CURRENCY_OPTIONS: readonly FlagSelectOption<CurrencyCodeOption>[] = [
  { value: 'usd', label: 'USD', flagUrl: `${FLAG_BASE}/flag-usa.png` },
  { value: 'gbp', label: 'GBP', flagUrl: `${FLAG_BASE}/flag-uk.png` },
  { value: 'eur', label: 'EUR', flagUrl: `${FLAG_BASE}/flag-ireland.png` },
];

@Component({
  selector: 'admin-tool-brand-vouchers-create',
  imports: [ReactiveFormsModule],
  templateUrl: './brand-vouchers-create.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AdminBrandVouchersCreate implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly voucherService = inject(VoucherService);
  private readonly toast = inject(ToastNotificationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly currencyOptions = CURRENCY_OPTIONS;
  readonly submitting = signal(false);

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    amount: [0, [Validators.required, Validators.min(0.01)]],
    currencyCode: ['gbp' as CurrencyCodeOption, Validators.required],
    expiresAt: [AdminBrandVouchersCreate.defaultExpiryIso(), Validators.required],
  });

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  currencySymbol(): string {
    return getCurrencySymbol(this.form.controls.currencyCode.value.toUpperCase(), 'narrow', 'en');
  }

  selectedCurrencyOption(): FlagSelectOption<CurrencyCodeOption> | null {
    const code = this.form.controls.currencyCode.value;
    return this.currencyOptions.find((option) => option.value === code) ?? null;
  }

  onCurrencySelected(value: CurrencyCodeOption): void {
    this.form.controls.currencyCode.setValue(value);
    this.cdr.detectChanges();
  }

  submit(): void {
    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    this.submitting.set(true);
    this.voucherService
      .create({
        name: value.name.trim(),
        amount: Math.round(Number(value.amount) * 100),
        currencyCode: value.currencyCode,
        expiresAt: value.expiresAt,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.submitting.set(false);
          this.form.reset({
            name: '',
            amount: 0,
            currencyCode: 'gbp',
            expiresAt: AdminBrandVouchersCreate.defaultExpiryIso(),
          });
          this.toast.alert({
            title: 'Voucher created',
            message: 'Brands can redeem this code once from their wallet.',
            type: 'success',
            icon: 'success',
          });
          void this.router.navigate([
            getAdminSettingsToolPath('brand-vouchers'),
            'list',
            created.id,
          ]);
          this.cdr.detectChanges();
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.submitting.set(false);
          const message = err?.error?.message || err?.message || 'Could not create the voucher.';
          this.toast.alert({
            title: 'Could not create voucher',
            message: Array.isArray(message) ? message.join(', ') : String(message),
            type: 'error',
            icon: 'error',
          });
          this.cdr.detectChanges();
        },
      });
  }

  private static defaultExpiryIso(): string {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
